import * as React from 'react';
import { useState, useEffect } from 'react';
import { Select, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, MenuItem } from '@mui/material';
import Header from '../components/header';
import { Printer } from '../models/printer';
import { getPrinters, deletePrinter, putPrinter, getStores } from '../utilities/api-utils';
import store, { useAppSelector } from '../store/index';
import { PrinterPayload } from '../models/printer';
import { getSlugId } from '../utilities/model-utils';
import { IStore } from '../models/store';

export default function PrintersView() {
  const { activeOrganization } = useAppSelector(store => store.organization);
  const [printers, setPrinters] = useState<Array<Printer>>([]);
  const [selectedPrinter, setSelectedPrinter] = useState<Printer | undefined>();
  const [createDialog, setCreateDialog] = useState<boolean>(false);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [editPrinterStoreId, setEditPrinterStoreId] = useState<string>('');
  const [editPrinterSerial, setEditPrinterSerial] = useState<string>('');
  const [editPrinterPassword, setEditPrinterPassword] = useState<string>('');
  const [stores, setStores] = useState<IStore[]>([]);
  
  const getAllPrinters = async(filteredStores: IStore[]) => {
    const response = await getPrinters(store.getState().authorization.cloudKey);
    const tempPrinters = [];
    for (const printer of response.data) {
      const concretePrinter = new Printer(printer);
      const store = filteredStores.find(x => x.id === printer.store_id);
      concretePrinter.store_name = store?.name || printer.store_id;
      tempPrinters.push(concretePrinter);
    }
    setPrinters(tempPrinters);
  };

  useEffect(() => {
    const fetchPrinters = async () => {
      await getStores((store.getState().authorization.cloudKey)).then((response) => {
        const filteredStores = response.data.filter(x => x.organization_id === activeOrganization);
        setStores(filteredStores);
        setEditPrinterStoreId(filteredStores[0].id);
        getAllPrinters(filteredStores);
      });
    };

    fetchPrinters();
  }, [activeOrganization]);

  const storeSelected = async (id: string) => {
    setEditPrinterStoreId(id);
  };

  const onSubmit = async() => {
    await getAllPrinters(stores);
  };

  const handleDelete = async () => {
    await deletePrinter(store.getState().authorization.cloudKey, selectedPrinter!.serial_number);
    setDeleteDialog(false);
    await onSubmit();
  };

  const addPrinter = async () => {
    const payload: PrinterPayload = {
      id: getSlugId(),
      store_id: editPrinterStoreId,
      serial_number: editPrinterSerial,
      password: editPrinterPassword
    };
    await putPrinter(store.getState().authorization.cloudKey, payload);
    setCreateDialog(false);
    await onSubmit();
  };
  
  return (
    <div>
      <Header title='Printers' />
      <Box 
        sx={{
          height: '500px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
        <Button
          variant='contained'
          sx={{
            marginTop: '15px',
            marginBottom: '15px',
            fontSize: '20px',
            width: '180px'}}
            onClick={() => {
              setCreateDialog(true);
            }}>
            Create Printer
        </Button>
        <TableContainer component={Paper} sx={{maxHeight: '480px'}}>
          <Table
            sx={{minWidth: '600px'}}>
            <TableHead>
              <TableRow>
                <TableCell align='left'>Printer Id</TableCell>
                <TableCell align='left'>Printer Serial</TableCell>
                <TableCell align='left'>Store</TableCell>
                <TableCell align='left'>Created At</TableCell>
                <TableCell align='center'>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {printers.map((printer, index) => (
                  <TableRow key={index}>
                    <TableCell >{printer.id}</TableCell>
                    <TableCell >{printer.serial_number}</TableCell>
                    <TableCell >{printer.store_name}</TableCell>
                    <TableCell >{printer.created_at}</TableCell>
                    <TableCell align='center' sx={{padding: '0'}}>
                      <Button variant='contained' onClick={() => {
                        setSelectedPrinter(printer);
                        setDeleteDialog(true);
                      }}>Delete</Button>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Dialog
        maxWidth='md'
        open={createDialog}
        onClose={() => setCreateDialog(false)}>
        <DialogTitle>
          <div style={{
            fontWeight: '500',
            color: '#121F35',
            fontSize: '40px'
          }}>Create Printer</div>
        </DialogTitle>
        <DialogContent sx={{
          height: '410px',
          width: '480px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'start',
          alignItems: 'start'}}>
          <Box sx={{
            height: '400px',
            width: '470px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'start',
            alignItems: 'start' }}>
              <Typography variant='h6'>Select Store</Typography>
              <Select
                sx={{
                  color: 'black',
                  width: '300px',
                  marginRight: '20px',
                  marginBottom: '20px',
                  height: '60px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'black !important',
                  },
                  '& .MuiSelect-iconOutlined': {
                    color: 'black'
                  }
                }}
                value={editPrinterStoreId}
                onChange={async(e) => {
                  await storeSelected(e.target.value);
                }}>
                  {stores.map((selectStore, index) => (
                    <MenuItem 
                      key={index}
                      value={selectStore.id}>
                        {selectStore.name}
                    </MenuItem>
                  ))}
              </Select>
              <Typography variant='h6'>Enter Printer Serial</Typography>
            <TextField inputProps={{ maxLength: 36 }} sx={{marginBottom: '20px', width: '300px'}} value={editPrinterSerial} onChange={(e) => setEditPrinterSerial(e.target.value)}/>
            <Typography variant='h6'>Enter Printer Password</Typography>
            <TextField inputProps={{ maxLength: 36 }} sx={{marginBottom: '30px', width: '300px'}} value={editPrinterPassword} onChange={(e) => setEditPrinterPassword(e.target.value)}/>
            <Button
              disabled={!editPrinterPassword || !editPrinterSerial || !editPrinterStoreId}
              variant='contained'
              sx={{fontSize: '20px'}}
              onClick={async() => await addPrinter()}>Create
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this printer? It cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>{
            setDeleteDialog(false);
          }}>Cancel</Button>
          <Button onClick={async()=>{
            await handleDelete();
          }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
